import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, FlaskConical, Package2 } from "lucide-react";
import { PageHero } from "@/components/SectionHeader";
import { PRODUCTS } from "@/lib/siteData";

export default function Products() {
  return (
    <div data-testid="products-page">
      <PageHero
        testid="products-hero"
        eyebrow="Our Products"
        title="DOTP · DOP · DINP · TOTM"
        description="Four core plasticisers, each engineered for a specific performance envelope across the flexible PVC value chain."
      />

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 space-y-10">
        {PRODUCTS.map((p, idx) => (
          <article
            key={p.code}
            data-testid={`product-card-${p.code.toLowerCase()}`}
            className="relative bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="absolute top-0 left-0 right-0 h-1 gradient-accent" />
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 p-8 lg:p-10">
              <div className="lg:col-span-3">
                <div className="flex items-start gap-3">
                  <span className="w-12 h-12 rounded-xl gradient-accent-soft flex items-center justify-center text-indigo-600">
                    <FlaskConical size={22} />
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-indigo-600">
                      Product 0{idx + 1}
                    </p>
                    <p className="font-display text-4xl font-bold uppercase text-slate-900 mt-1 leading-none">
                      {p.code}
                    </p>
                  </div>
                </div>
                <p className="text-sm text-slate-500 mt-3">{p.name}</p>
                {p.brandName && (
                  <p
                    className="text-xs font-semibold text-indigo-600 mt-1"
                    data-testid={`product-${p.code.toLowerCase()}-brand`}
                  >
                    Brand: {p.brandName}
                  </p>
                )}
                <div className="mt-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 rounded-full px-3 py-1">
                  CAS {p.cas}
                </div>
              </div>

              <div className="lg:col-span-6">
                <h3 className="font-display text-xl uppercase font-bold text-slate-900">
                  Overview
                </h3>
                <p className="mt-3 text-slate-700 leading-relaxed">
                  {p.description}
                </p>

                <h4 className="font-display text-sm uppercase tracking-widest text-indigo-600 mt-6">
                  Applications
                </h4>
                <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700">
                  {p.applications.map((app) => (
                    <li
                      key={app}
                      data-testid={`product-${p.code.toLowerCase()}-application`}
                      className="flex items-center gap-2"
                    >
                      <span className="w-1.5 h-1.5 rounded-full gradient-accent" />
                      {app}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="lg:col-span-3 flex flex-col gap-4">
                {p.image && (
                  <div
                    className="rounded-xl overflow-hidden border border-slate-100 bg-slate-50"
                    data-testid={`product-${p.code.toLowerCase()}-image`}
                  >
                    <img
                      src={p.image}
                      alt={`${p.code} ${p.brandName ? `(${p.brandName}) ` : ""}drum packaging`}
                      className="w-full h-48 object-contain p-3"
                    />
                  </div>
                )}
                <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 flex items-center gap-2">
                    <Package2 size={14} /> Packaging
                  </p>
                  <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                    {p.packaging}
                  </p>
                </div>
                <Link
                  to="/quote"
                  data-testid={`product-${p.code.toLowerCase()}-quote-link`}
                  className="mt-auto"
                >
                  <Button className="w-full gradient-accent text-white font-semibold uppercase tracking-wider rounded-md hover:opacity-90 transition-opacity">
                    Request a Quote
                    <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}
